import { useState, useCallback } from 'react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { platformLabel } from '@/lib/platforms'
import { PlatformTab } from './PlatformTab'
import type { PlatformCapability, PlatformOverrideData } from './PlatformTab'

/**
 * Default platform capabilities used when the backend endpoint is unavailable.
 * Sourced from PLATFORM_CAPABILITIES in app/schemas/content/base.py.
 */
const DEFAULT_CAPABILITIES: Record<string, PlatformCapability> = {
  twitter: {
    max_length: 280,
    supported_media: ['image', 'video', 'gif'],
    features: {
      hashtags: true,
      mentions: true,
      first_comment: false,
      threads: true,
      article_link: false,
    },
  },
  instagram: {
    max_length: 2200,
    supported_media: ['image', 'video', 'carousel'],
    features: {
      hashtags: true,
      mentions: true,
      first_comment: true,
      threads: false,
      article_link: false,
    },
  },
  facebook: {
    max_length: 63206,
    supported_media: ['image', 'video', 'link'],
    features: {
      hashtags: true,
      mentions: true,
      first_comment: false,
      threads: false,
      article_link: true,
    },
  },
  linkedin: {
    max_length: 3000,
    supported_media: ['image', 'video', 'document'],
    features: {
      hashtags: true,
      mentions: true,
      first_comment: false,
      threads: false,
      article_link: true,
    },
  },
  tiktok: {
    max_length: 2200,
    supported_media: ['video'],
    features: {
      hashtags: true,
      mentions: true,
      first_comment: false,
      threads: false,
      article_link: false,
    },
  },
  pinterest: {
    max_length: 500,
    supported_media: ['image', 'video'],
    features: {
      hashtags: true,
      mentions: false,
      first_comment: false,
      threads: false,
      article_link: true,
    },
  },
  youtube: {
    max_length: 5000,
    supported_media: ['video'],
    features: {
      hashtags: true,
      mentions: false,
      first_comment: false,
      threads: false,
      article_link: false,
    },
  },
  gmb: {
    max_length: 1500,
    supported_media: ['image'],
    features: {
      hashtags: false,
      mentions: false,
      first_comment: false,
      threads: false,
      article_link: true,
    },
  },
}

const ALL_PLATFORMS_KEY = '__all__'

function createEmptyOverride(baseBody: string): PlatformOverrideData {
  return { body: baseBody, first_comment: '', thread: false, article_link: '' }
}

interface PlatformCustomizerProps {
  platforms: string[]
  baseBody: string
  mediaUrl?: string
  onSubmit: (overrides: Record<string, PlatformOverrideData>) => void
  onCancel: () => void
}

export function PlatformCustomizer({
  platforms,
  baseBody,
  mediaUrl,
  onSubmit,
  onCancel,
}: PlatformCustomizerProps) {
  const [activeTab, setActiveTab] = useState<string>(ALL_PLATFORMS_KEY)

  // Initialize per-platform overrides from base body
  const [overrides, setOverrides] = useState<Record<string, PlatformOverrideData>>(() => {
    const initial: Record<string, PlatformOverrideData> = {}
    for (const platform of platforms) {
      initial[platform] = createEmptyOverride(baseBody)
    }
    return initial
  })

  // Track which platforms have been individually edited (override the "all" tab)
  const [edited, setEdited] = useState<Set<string>>(new Set())

  // Sync base body into overrides for non-edited platforms whenever baseBody,
  // the platform list, or the edited set changes. Done with a render-time change
  // guard (React's recommended alternative to a setState-in-effect sync) that
  // mirrors the previous effect's dependency array exactly.
  const [syncedFrom, setSyncedFrom] = useState({ baseBody, platforms, edited })
  if (
    syncedFrom.baseBody !== baseBody ||
    syncedFrom.platforms !== platforms ||
    syncedFrom.edited !== edited
  ) {
    setSyncedFrom({ baseBody, platforms, edited })
    setOverrides((prev) => {
      const next = { ...prev }
      for (const platform of platforms) {
        if (!edited.has(platform)) {
          next[platform] = { ...next[platform], body: baseBody }
        }
      }
      return next
    })
  }

  const handleAllPlatformsChange = useCallback(
    (data: PlatformOverrideData) => {
      // Propagate body to all non-edited platforms
      setOverrides((prev) => {
        const next: Record<string, PlatformOverrideData> = {}
        for (const platform of platforms) {
          if (edited.has(platform)) {
            next[platform] = prev[platform]
          } else {
            next[platform] = { ...prev[platform], body: data.body }
          }
        }
        return next
      })
    },
    [platforms, edited]
  )

  const handlePlatformChange = useCallback((platform: string, data: PlatformOverrideData) => {
    setEdited((prev) => new Set(prev).add(platform))
    setOverrides((prev) => ({ ...prev, [platform]: data }))
  }, [])

  const handleSubmit = useCallback(() => {
    onSubmit(overrides)
  }, [overrides, onSubmit])

  // Check if any platform exceeds its character limit
  const hasOverLimit = platforms.some((p) => {
    const cap = DEFAULT_CAPABILITIES[p]
    return cap && overrides[p] && overrides[p].body.length > cap.max_length
  })

  // Get the "all platforms" body (use first non-edited platform's body, or base)
  const allBody = platforms.find((p) => !edited.has(p))
    ? (overrides[platforms.find((p) => !edited.has(p))!]?.body ?? baseBody)
    : baseBody

  return (
    <div className="space-y-3" data-testid="platform-customizer">
      {/* Tab bar — following MusicPicker TabButton pattern */}
      <div className="bg-muted flex gap-1 overflow-x-auto rounded-lg p-1">
        <TabButton
          label="All Platforms"
          isActive={activeTab === ALL_PLATFORMS_KEY}
          onClick={() => setActiveTab(ALL_PLATFORMS_KEY)}
          testId="tab-all-platforms"
        />
        {platforms.map((platform) => (
          <TabButton
            key={platform}
            label={platformLabel(platform)}
            isActive={activeTab === platform}
            onClick={() => setActiveTab(platform)}
            testId={`tab-${platform}`}
            hasOverride={edited.has(platform)}
          />
        ))}
      </div>

      {/* Tab content */}
      <div className="min-h-[160px]">
        {activeTab === ALL_PLATFORMS_KEY ? (
          <PlatformTab
            platform="all"
            capabilities={{
              max_length: Math.min(
                ...platforms.map((p) => DEFAULT_CAPABILITIES[p]?.max_length ?? 5000)
              ),
              supported_media: ['image', 'video'],
              features: {
                hashtags: false,
                mentions: false,
                first_comment: false,
                threads: false,
                article_link: false,
              },
            }}
            data={createEmptyOverride(allBody)}
            onChange={handleAllPlatformsChange}
            mediaUrl={mediaUrl}
          />
        ) : (
          <PlatformTab
            platform={activeTab}
            capabilities={DEFAULT_CAPABILITIES[activeTab] ?? DEFAULT_CAPABILITIES.twitter}
            data={overrides[activeTab] ?? createEmptyOverride(baseBody)}
            onChange={(data) => handlePlatformChange(activeTab, data)}
            mediaUrl={mediaUrl}
          />
        )}
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-2 pt-1">
        <Button variant="outline" size="sm" onClick={onCancel} className="text-xs">
          Back
        </Button>
        <Button size="sm" onClick={handleSubmit} disabled={hasOverLimit} className="text-xs">
          Confirm & Schedule
        </Button>
      </div>
    </div>
  )
}

// --- TabButton (following MusicPicker pattern) ---

interface TabButtonProps {
  label: string
  isActive: boolean
  onClick: () => void
  testId: string
  hasOverride?: boolean
}

function TabButton({ label, isActive, onClick, testId, hasOverride }: TabButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium whitespace-nowrap transition-colors',
        isActive
          ? 'bg-background text-foreground shadow-sm'
          : 'text-muted-foreground hover:text-foreground'
      )}
      data-testid={testId}
    >
      {label}
      {hasOverride && <span className="bg-primary size-1.5 rounded-full" title="Custom override" />}
    </button>
  )
}
